import {
  Text,
  Group,
  Kbd,
  Loader,
  Modal,
  Title,
  SimpleGrid,
  Button,
} from "@mantine/core";
import { FC, useCallback, useEffect, useState } from "react";
import { Time } from "../../../../../Components/Time";
import { useMutation } from "@tanstack/react-query";
import {
  gameRevokeGoal,
  gameCorrectGoal,
} from "../../../../../openapi/api-client";
import { PlayerNumber } from "../../../../../Components/PlayerNumber";
import { PlayerName } from "../../../../../Components/PlayerName";
import { useGame } from "../../../../../hooks/useGame.ts";
import { usePlayerListByTeam } from "../../../../../hooks/usePlayerListByTeam.ts";
import { useCompetition } from "../../../../../hooks/useCompetition.ts";
import { PlayerButton } from "../PlayerButton";
import { TeamName } from "../../../../../Components/TeamName";

interface GameProps {
  gameId: string;
  goalId: string;
}

export const Goal: FC<GameProps> = (props) => {
  const { gameId, goalId } = props;

  const [modalOpened, setModalOpened] = useState(false);
  const [editStep, setEditStep] = useState<"scorer" | "assist">("scorer");
  const [selectedScorer, setSelectedScorer] = useState<string | undefined>();
  const [isOwnGoal, setIsOwnGoal] = useState(false);
  const [typedNumber, setTypedNumber] = useState("");

  const revokeGoalMutation = useMutation({ mutationFn: gameRevokeGoal });
  const correctGoalMutation = useMutation({ mutationFn: gameCorrectGoal });

  const game = useGame(gameId);
  const competition = useCompetition(game.data?.competitionId);

  const goal = game.data?.goals.find((g) => g.goalId == goalId);

  const scoringTeamId = goal?.homeTeam
    ? game.data?.actualTeams.homeTeamId
    : game.data?.actualTeams.guestTeamId;
  const otherTeamId = goal?.homeTeam
    ? game.data?.actualTeams.guestTeamId
    : game.data?.actualTeams.homeTeamId;

  const teamIdForScorer = isOwnGoal ? otherTeamId : scoringTeamId;
  const players = usePlayerListByTeam(teamIdForScorer);

  const isReady = game.isSuccess && competition.isSuccess && goal;

  const closeModal = useCallback(() => {
    setModalOpened(false);
    setSelectedScorer(undefined);
    setEditStep("scorer");
  }, []);

  const handleScorerSelect = useCallback(
    (playerId: string) => {
      if (!isReady) return;
      if (!competition.data.settings.topscorerCountAssists) {
        void correctGoalMutation
          .mutateAsync({
            id: gameId,
            goalId,
            playerId,
            isOwnGoal,
          })
          .then(() => {
            closeModal();
          });
        return;
      }

      setSelectedScorer(playerId);
      setEditStep("assist");
    },
    [
      isReady,
      competition.data,
      correctGoalMutation,
      gameId,
      goalId,
      isOwnGoal,
      closeModal,
    ],
  );

  const handleAssistSelect = useCallback(
    (assistPlayerId?: string) => {
      if (!selectedScorer) return;

      void correctGoalMutation
        .mutateAsync({
          id: gameId,
          goalId,
          playerId: selectedScorer,
          assistPlayerId,
          isOwnGoal,
        })
        .then(() => {
          closeModal();
        });
    },
    [
      selectedScorer,
      correctGoalMutation,
      gameId,
      goalId,
      isOwnGoal,
      closeModal,
    ],
  );

  // Keyboard input for jersey numbers
  const selectPlayerByNumber = useCallback(
    (number: number) => {
      if (!players.isSuccess || correctGoalMutation.isPending) return;

      const player = players.data.find((p) => p.number === number);
      if (!player) return;

      // Don't allow selecting the scorer as assist
      if (editStep === "assist" && player.id === selectedScorer) return;

      if (editStep === "scorer") {
        handleScorerSelect(player.id);
      } else {
        handleAssistSelect(player.id);
      }
    },
    [
      players.isSuccess,
      players.data,
      correctGoalMutation.isPending,
      editStep,
      selectedScorer,
      handleScorerSelect,
      handleAssistSelect,
    ],
  );

  // Reset typed number when modal closes or step changes
  useEffect(() => {
    setTypedNumber("");
  }, [modalOpened, editStep]);

  // Handle keyboard input
  useEffect(() => {
    if (!modalOpened) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore key repeats (holding down key)
      if (e.repeat) return;

      if (/^\d$/.test(e.key)) {
        e.preventDefault();
        setTypedNumber((prev) => prev + e.key);
        return;
      }

      if (e.key === "Enter" && typedNumber) {
        e.preventDefault();
        const number = parseInt(typedNumber, 10);
        if (!isNaN(number)) {
          selectPlayerByNumber(number);
        }
        setTypedNumber("");
        return;
      }

      if (e.key === "Backspace" && typedNumber) {
        e.preventDefault();
        setTypedNumber((prev) => prev.slice(0, -1));
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalOpened, typedNumber, selectPlayerByNumber]);

  if (!game.isSuccess || !competition.isSuccess) {
    return <Loader />;
  }

  if (!goal) {
    throw new Error("goal not found");
  }

  const openEditModal = () => {
    setIsOwnGoal(goal.isOwnGoal);
    setSelectedScorer(undefined);
    setEditStep("scorer");
    setModalOpened(true);
  };

  return (
    <>
      <Text c="dimmed" size="sm">
        {goal.playerId ? (
          <>
            Nr. <PlayerNumber playerId={goal.playerId} />,{" "}
            <PlayerName
              variant={"firstname lastname"}
              playerId={goal.playerId}
            />
            {goal.assistPlayerId && (
              <>
                {" "}
                (Vorlage: Nr. <PlayerNumber
                  playerId={goal.assistPlayerId}
                />,{" "}
                <PlayerName
                  variant={"firstname lastname"}
                  playerId={goal.assistPlayerId}
                />
                )
              </>
            )}
          </>
        ) : (
          <Text c="orange" fw={500} component="span">
            Torschütze nicht erfasst
          </Text>
        )}
      </Text>
      <Text size="xs" mt={4}>
        <Group>
          <Time time={goal.time} />
          <Text
            variant="link"
            c={goal.playerId ? "dimmed" : "orange"}
            fw={goal.playerId ? undefined : 500}
            tabIndex={0}
            role="link"
            style={{ cursor: "pointer" }}
            onClick={openEditModal}
          >
            {goal.playerId ? "Bearbeiten" : "Torschütze zuordnen"}
          </Text>
          {!game.data.regularTimeFreezed && (
            <Text
              variant="link"
              c="dimmed"
              tabIndex={0}
              role="link"
              style={{ cursor: "pointer" }}
              onClick={() =>
                void revokeGoalMutation.mutateAsync({ id: gameId, goalId })
              }
            >
              Löschen
            </Text>
          )}
        </Group>
      </Text>

      <Modal
        size="xl"
        opened={modalOpened}
        onClose={closeModal}
        centered
        closeOnClickOutside={false}
        closeOnEscape={true}
        trapFocus={true}
        withCloseButton={false}
      >
        <Title mb="md">
          {editStep === "scorer" ? (
            <>
              {goal.playerId ? "Torschütze korrigieren" : "Torschütze zuordnen"}{" "}
              für <TeamName teamId={scoringTeamId!} />
            </>
          ) : (
            <>
              Vorlage auswählen für <TeamName teamId={scoringTeamId!} />
            </>
          )}
        </Title>
        <Text size="sm" c="dimmed" mb="md">
          Trikotnummer + <Kbd>Enter</Kbd>:{" "}
          <Kbd style={{ minWidth: 40, textAlign: "center" }}>
            {typedNumber || "—"}
          </Kbd>
        </Text>

        <SimpleGrid cols={5} spacing="xl">
          {!players.isSuccess ? (
            <Loader />
          ) : (
            <>
              {players.data.map((p) => (
                <PlayerButton
                  key={p.id}
                  onClick={() => {
                    editStep === "scorer"
                      ? handleScorerSelect(p.id)
                      : handleAssistSelect(p.id);
                  }}
                  number={p.number}
                  color={editStep === "scorer" ? "blue" : "green"}
                  disabled={
                    correctGoalMutation.isPending ||
                    (editStep === "assist" && p.id === selectedScorer)
                  }
                />
              ))}
              {editStep === "scorer" && (
                <Button
                  fullWidth
                  color="red"
                  disabled={correctGoalMutation.isPending}
                  onClick={() => {
                    setIsOwnGoal(!isOwnGoal);
                  }}
                  styles={() => ({
                    root: {
                      height: 100,
                      fontSize: 20,
                      textAlign: "center",
                      lineHeight: "1.3em",
                    },
                  })}
                >
                  {isOwnGoal ? (
                    <>
                      Kein
                      <br />
                      Eigentor
                    </>
                  ) : (
                    "Eigentor"
                  )}
                </Button>
              )}
              {editStep === "assist" && (
                <Button
                  fullWidth
                  color="red"
                  disabled={correctGoalMutation.isPending}
                  onClick={() => {
                    handleAssistSelect(undefined);
                  }}
                  styles={() => ({
                    root: {
                      height: 100,
                      fontSize: 20,
                      textAlign: "center",
                      lineHeight: "1.3em",
                    },
                  })}
                >
                  keine
                  <br />
                  Vorlage
                </Button>
              )}
            </>
          )}
        </SimpleGrid>
        <Button mt="xl" color="red" variant="outline" onClick={closeModal}>
          Abbrechen
        </Button>
      </Modal>
    </>
  );
};
